import React, { useState } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';

import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectTrigger, SelectValue, SelectItem } from '@/components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Separator } from '@/components/ui/separator';

import { ChevronDown, ChevronUp, AlertTriangle, User, Clock, FileText, MessageSquare, History, Trash2, Star, Save } from 'lucide-react';

import { SupervisorReportsList } from '@/components/supervisor-reports/SupervisorReportsList';
import { ReportsFilters } from '@/components/reports/ReportsFilters';

import { useUserRoles } from '@/hooks/useUserRoles';
import { useToast } from '@/hooks/use-toast';
import { useQuery } from '@tanstack/react-query';

import { 
  useSupervisorReports, 
  useUpdateSupervisorReportStatus, 
  useAddSupervisorReportNote, 
  useDeleteSupervisorReport,
  useSupervisorReportHistory,
  useUpdateGuardEvaluation,
  GuardEvaluation
} from '@/hooks/useSupervisorReports';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';

interface SupervisorReportsViewProps {
  teamId: string;
}

// Star Rating Component for Guard Evaluations
const StarRating: React.FC<{
  rating: number;
  onRatingChange: (rating: number) => void;
  label: string;
  description: string;
  disabled?: boolean;
}> = ({ rating, onRatingChange, label, description, disabled = false }) => {
  const [hoverRating, setHoverRating] = useState(0);

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium">{label}</label>
      <p className="text-xs text-muted-foreground">{description}</p>
      <div className="flex items-center space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            disabled={disabled}
            className={`p-1 rounded-sm transition-colors ${
              disabled ? 'cursor-not-allowed' : 'hover:bg-muted cursor-pointer'
            }`}
            onMouseEnter={() => !disabled && setHoverRating(star)}
            onMouseLeave={() => !disabled && setHoverRating(0)}
            onClick={() => !disabled && onRatingChange(star)}
          >
            <Star
              className={`w-6 h-6 ${
                star <= (hoverRating || rating)
                  ? 'fill-yellow-400 text-yellow-400'
                  : 'text-gray-300'
              }`}
            />
          </button>
        ))}
        <span className="ml-2 text-sm text-muted-foreground">
          {rating > 0 ? `${rating}/5` : 'Not rated'}
        </span>
      </div>
      {rating > 0 && (
        <div className="text-xs">
          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
            rating < 3 
              ? 'bg-red-100 text-red-800' 
              : rating < 4 
                ? 'bg-yellow-100 text-yellow-800'
                : 'bg-green-100 text-green-800'
          }`}>
            {rating < 3 ? (
              <>🔻 Needs Improvement</>
            ) : rating < 4 ? (
              '⚠️ Satisfactory'
            ) : rating < 4.5 ? (
              '✅ Good'
            ) : (
              '⭐ Excellent'
            )}
          </span>
        </div>
      )}
    </div>
  );
};

// Guard Evaluation Section Component
const GuardEvaluationSection: React.FC<{
  reportId: string;
  guardId: string | null;
  guardName: string;
  currentEvaluation?: GuardEvaluation;
  currentUser: any;
  onEvaluationUpdate: () => void;
}> = ({ reportId, guardId, guardName, currentEvaluation, currentUser, onEvaluationUpdate }) => {
  const [performanceRating, setPerformanceRating] = useState(currentEvaluation?.performance_rating || 0);
  const [complianceStatus, setComplianceStatus] = useState(currentEvaluation?.compliance_status || 0);
  const [evaluationNotes, setEvaluationNotes] = useState(currentEvaluation?.notes || '');
  const [isEvaluating, setIsEvaluating] = useState(false);
  
  const updateEvaluation = useUpdateGuardEvaluation();
  const { toast } = useToast();

  const handleSaveEvaluation = async () => {
    if (!guardId || !currentUser) {
      toast({
        title: "Error",
        description: "Cannot save evaluation. Missing guard or user information.",
        variant: "destructive"
      });
      return;
    }

    if (performanceRating === 0 && complianceStatus === 0) {
      toast({
        title: "Validation Error",
        description: "Please provide at least one rating (Performance or Compliance).",
        variant: "destructive"
      });
      return;
    }

    const userName = currentUser.profile?.full_name || 
                     `${currentUser.profile?.first_name} ${currentUser.profile?.last_name}` || 
                     currentUser.email || 'Unknown User';

    const evaluation: GuardEvaluation = {
      performance_rating: performanceRating > 0 ? performanceRating : undefined,
      compliance_status: complianceStatus > 0 ? complianceStatus : undefined,
      notes: evaluationNotes.trim() || undefined,
      evaluation_date: new Date().toISOString()
    };

    try {
      await updateEvaluation.mutateAsync({
        reportId,
        evaluation,
        userId: currentUser.id,
        userName
      });
      
      setIsEvaluating(false);
      onEvaluationUpdate();
      
      toast({
        title: "Success",
        description: "Guard evaluation saved successfully!"
      });
    } catch (error) {
      console.error('Error saving evaluation:', error);
    }
  };

  const getOverallStatus = () => {
    if (performanceRating === 0 && complianceStatus === 0) return 'No Evaluation';
    
    const avg = performanceRating > 0 && complianceStatus > 0 
      ? (performanceRating + complianceStatus) / 2
      : performanceRating > 0 
        ? performanceRating 
        : complianceStatus;
    
    if (avg < 3) return 'Below Standards';
    if (avg < 4) return 'Meets Standards';
    if (avg < 4.5) return 'Above Standards';
    return 'Exceptional';
  };

  if (!guardId) {
    return (
      <div className="p-4 bg-muted/30 rounded-lg text-center text-muted-foreground">
        <User className="w-8 h-8 mx-auto mb-2 opacity-50" />
        <p className="text-sm">No guard assigned to this report</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4 border rounded-lg bg-gradient-to-r from-blue-50 to-indigo-50">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <User className="w-5 h-5 text-blue-600" />
          <h6 className="font-semibold text-lg text-blue-800">Guard Evaluation: {guardName}</h6>
        </div>
        <span className={`text-sm px-3 py-1 rounded-full font-medium ${
          getOverallStatus() === 'Below Standards' 
            ? 'bg-red-100 text-red-800'
            : getOverallStatus() === 'Meets Standards'
              ? 'bg-yellow-100 text-yellow-800'
              : getOverallStatus() === 'Above Standards'
                ? 'bg-blue-100 text-blue-800'
                : getOverallStatus() === 'Exceptional'
                  ? 'bg-green-100 text-green-800'
                  : 'bg-gray-100 text-gray-800'
        }`}>
          {getOverallStatus()}
        </span>
      </div>

      {!isEvaluating && (performanceRating > 0 || complianceStatus > 0) ? (
        // Display existing evaluation
        <div className="space-y-3">
          {performanceRating > 0 && (
            <div className="flex items-center justify-between p-3 bg-white rounded-lg">
              <span className="font-medium">Αξιολόγηση Απόδοσης (Performance):</span>
              <div className="flex items-center space-x-2">
                <div className="flex space-x-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                      key={star}
                      className={`w-4 h-4 ${
                        star <= performanceRating
                          ? 'fill-yellow-400 text-yellow-400'
                          : 'text-gray-300'
                      }`}
                    />
                  ))}
                </div>
                <span className="font-medium">{performanceRating}/5</span>
              </div>
            </div>
          )}
          
          {complianceStatus > 0 && (
            <div className="flex items-center justify-between p-3 bg-white rounded-lg">
              <span className="font-medium">Κατάσταση Συμόρφωσης (Compliance):</span>
              <div className="flex items-center space-x-2">
                <div className="flex space-x-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                      key={star}
                      className={`w-4 h-4 ${
                        star <= complianceStatus
                          ? 'fill-yellow-400 text-yellow-400'
                          : 'text-gray-300'
                      }`}
                    />
                  ))}
                </div>
                <span className="font-medium">{complianceStatus}/5</span>
              </div>
            </div>
          )}
          
          {evaluationNotes && (
            <div className="p-3 bg-white rounded-lg">
              <span className="font-medium text-sm text-muted-foreground">Notes:</span>
              <p className="text-sm mt-1">{evaluationNotes}</p>
            </div>
          )}
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsEvaluating(true)}
            className="w-full"
          >
            Update Evaluation
          </Button>
        </div>
      ) : (
        // Evaluation form
        <div className="space-y-4">
          <StarRating
            rating={performanceRating}
            onRatingChange={setPerformanceRating}
            label="Αξιολόγηση Απόδοσης (Performance Rating)"
            description="Rate the guard's overall job performance, work quality, and effectiveness"
            disabled={updateEvaluation.isPending}
          />
          
          <StarRating
            rating={complianceStatus}
            onRatingChange={setComplianceStatus}
            label="Κατάσταση Συμόρφωσης (Compliance Status)"
            description="Rate the guard's adherence to protocols, procedures, and regulations"
            disabled={updateEvaluation.isPending}
          />

          <div className="space-y-2">
            <label className="text-sm font-medium">Additional Notes (Optional)</label>
            <Textarea
              placeholder="Add any specific observations, recommendations, or comments about the guard's performance..."
              value={evaluationNotes}
              onChange={(e) => setEvaluationNotes(e.target.value)}
              rows={3}
              disabled={updateEvaluation.isPending}
            />
          </div>

          {(performanceRating > 0 || complianceStatus > 0) && (
            <div className="p-4 bg-white rounded-lg border">
              <h4 className="font-medium mb-2">Evaluation Summary</h4>
              <div className="space-y-1 text-sm">
                {performanceRating > 0 && (
                  <div className="flex justify-between">
                    <span>Performance Rating:</span>
                    <span className="font-medium">{performanceRating}/5 stars</span>
                  </div>
                )}
                {complianceStatus > 0 && (
                  <div className="flex justify-between">
                    <span>Compliance Status:</span>
                    <span className="font-medium">{complianceStatus}/5 stars</span>
                  </div>
                )}
                <div className="flex justify-between font-medium pt-2 border-t">
                  <span>Overall Status:</span>
                  <span>{getOverallStatus()}</span>
                </div>
              </div>
            </div>
          )}

          <div className="flex space-x-2">
            <Button
              onClick={handleSaveEvaluation}
              disabled={updateEvaluation.isPending}
              className="flex-1 flex items-center space-x-2"
            >
              <Save className="w-4 h-4" />
              <span>
                {updateEvaluation.isPending ? 'Saving...' : 'Save Evaluation'}
              </span>
            </Button>
            
            {isEvaluating && (
              <Button
                variant="outline"
                onClick={() => {
                  setIsEvaluating(false);
                  setPerformanceRating(currentEvaluation?.performance_rating || 0);
                  setComplianceStatus(currentEvaluation?.compliance_status || 0);
                  setEvaluationNotes(currentEvaluation?.notes || '');
                }}
                disabled={updateEvaluation.isPending}
              >
                Cancel
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Component to display selected guards with names
const SelectedGuardsDisplay: React.FC<{ guardIds: string[] }> = ({ guardIds }) => {
  const { data: guardNamesMap, isLoading } = useQuery({
    queryKey: ['selectedGuardNames', guardIds],
    queryFn: async () => {
      if (!guardIds || guardIds.length === 0) return {};
      
      const { data } = await supabase
        .from('profiles')
        .select('id, full_name, first_name, last_name')
        .in('id', guardIds);
      
      const nameMap: Record<string, string> = {};
      data?.forEach((profile) => {
        nameMap[profile.id] = profile.full_name || `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || 'Unknown Guard';
      });
      
      return nameMap;
    },
    enabled: guardIds && guardIds.length > 0
  });

  if (isLoading) {
    return <div className="text-xs text-muted-foreground">Loading guard names...</div>;
  }

  return (
    <div className="space-y-2">
      {guardIds.map((guardId, idx) => (
        <div key={idx} className="flex items-center gap-2">
          <span className="bg-primary/10 px-3 py-1 rounded-full text-sm font-medium">
            {guardNamesMap?.[guardId] || 'Unknown Guard'}
          </span>
          <span className="text-xs text-muted-foreground">
            (ID: {guardId.substring(0, 8)})
          </span>
        </div>
      ))}
    </div>
  );
};

export const SupervisorReportsView: React.FC<SupervisorReportsViewProps> = ({ teamId }) => {
  const [selectedTeam, setSelectedTeam] = useState<string | undefined>(teamId);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>();

  const handleReset = () => {
    setSelectedTeam(teamId);
    setSelectedDate(undefined);
  };

  const dateFilter = selectedDate ? {
    start: new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate(), 0, 0, 0),
    end: new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate(), 23, 59, 59)
  } : undefined;

  return (
    <div className="w-full max-w-none p-3 sm:p-6 space-y-6">
      <ReportsFilters
        selectedTeam={selectedTeam}
        onTeamChange={setSelectedTeam}
        selectedDate={selectedDate}
        onDateChange={setSelectedDate}
        onReset={handleReset}
        showTeamFilter={false} // Hide team filter since we're already in a team context
      />
      
      <SupervisorReportsList 
        teamId={selectedTeam || teamId} 
        dateFilter={dateFilter}
      />
    </div>
  );
};